import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EntityKeyByReference,
  EnumValues,
  Note,
  UserLite,
  EnumObjectExtended,
  GlobalEntityDefinition,
  BreadcrumbService,
} from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Observable, finalize, map, of, startWith } from 'rxjs';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { EntityKeyByNumber } from '../../../models/inventory/keys/entity-key';
import { LotItem } from '../../../models/inventory/lot-item';
import { LotItemSearchCommand, LotItemSearchResultItem } from '../../../models/inventory/lot-item-search';
import { ProductRecipe } from '../../../models/item/product-recipe';
import { Batch, BatchStatus, ProductionWorkOrder, ProductionWorkOrderStatus } from '../../../models/production/production-work-order';
import { Requisition, RequisitionEntry, RequisitionStatus } from '../../../models/requisitions/requisition';
import { AssignedLotItem, IngredientDisplayNode, RequisitionEntryFormGroup } from '../../../models/requisitions/requisition-details-form';
import {
  ENTITY_DEF_BATCH,
  FROM_LOCATION_CODE,
  LOT_ITEM_SEARCH_LOCATION,
  TO_LOCATION_CODE,
  VALID_BATCH_STATUSES,
  VALID_PWO_STATUSES,
} from '../../../models/requisitions/requisition.constants';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { InventoryTransactionProxyService } from '../../../proxies/inventory/inventory-transaction.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { RequisitionProxyService } from '../../../proxies/inventory/requisition.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMaster } from '../../../models/item/item-master';
import { ProductRecipeProxyService } from '../../../proxies/item/product-recipe.proxy.service';
import { BatchProxyService } from '../../../proxies/production/batch.proxy.service';
import { ProductionWorkOrderProxyService } from '../../../proxies/production/production-work-order.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { LocalHelperService } from '../../../services/local-helper.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-requisition-detail',
  templateUrl: './requisition-detail.component.html',
  styleUrl: './requisition-detail.component.scss',
})
export class RequisitionDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private breadcrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private requisitionProxyService = inject(RequisitionProxyService);
  private pwoProxyService = inject(ProductionWorkOrderProxyService);
  private batchProxyService = inject(BatchProxyService);
  private itemMasterProxyService = inject(ItemMasterProxyService);
  private productRecipeProxyService = inject(ProductRecipeProxyService);
  private enumService = inject(EnumService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);
  private userAuthService = inject(UserAuthenticationService);
  private localHelperService = inject(LocalHelperService);
  private businessProxyService = inject(BusinessProxyService);
  private lotItemProxyService = inject(LotItemProxyService);
  private inventoryTransactionProxyService = inject(InventoryTransactionProxyService);

  loading = signal<number>(0);
  isCreateMode = signal<boolean>(true);
  requisition = signal<Requisition | null>(null);
  productionWorkOrder = signal<ProductionWorkOrder | null>(null);
  selectedBatch = signal<Batch | null>(null);
  selectedBatchRecipe = signal<ProductRecipe | null>(null);
  batches = signal<Batch[]>([]);
  ingredientNodes = signal<IngredientDisplayNode[]>([]);
  resolvedItemNames = signal<Map<string, string>>(new Map());
  assignedLotItems = signal<Map<string, AssignedLotItem[]>>(new Map());
  additionalItems = signal<ItemMaster[]>([]);

  notes: Note[] = [];
  userLite: UserLite | null = null;
  statuses: EnumValues = {};
  statusesIcon: EnumValues = {};
  statusEnumObject?: EnumObjectExtended;
  unitOfMeasures: EnumValues = {};
  pwoStatusLabels: EnumValues = {};
  batchStatusLabels: EnumValues = {};
  originalStatus: number | null = null;
  entriesDirty = false;

  pwoSearchControl = new FormControl<string | ProductionWorkOrder>('');
  statusControl = new FormControl<number | null>(null);
  lotItemTargetItemCode = new FormControl<string>('', { nonNullable: true });
  lotItemSearchControl = new FormControl<string | LotItem>('');
  additionalItemControl = new FormControl<ItemMaster | null>(null);

  entriesFormArray = new FormArray<FormGroup<RequisitionEntryFormGroup>>([]);

  private entriesValue = toSignal(
    this.entriesFormArray.valueChanges.pipe(
      startWith(null),
      map(() => this.entriesFormArray.getRawValue()),
    ),
    { requireSync: true },
  );

  private entryItemCodes = computed(() => this.entriesValue().map((entry) => entry.itemCode));

  canEdit = computed(() => {
    return AuthService.hasRole(UserGroup.RequisitionEditor) || AuthService.hasRole(UserGroup.RequisitionAdmin);
  });

  canCreate = computed(() => {
    return this.selectedBatch() !== null && this.canEdit();
  });

  isProcessing = computed(() => {
    const req = this.requisition();
    return req?.status === RequisitionStatus.Processing;
  });

  entryItemCodeOptions = computed(() => {
    return this.entryItemCodes().map((itemCode) => {
      const code = itemCode || '';
      return { label: `${code} — ${this.getItemName(code)}`, value: code };
    });
  });

  showSaveButton = computed(() => {
    const req = this.requisition();
    if (!req) return this.isCreateMode();
    return req.status === RequisitionStatus.Requested;
  });

  entriesTotalQuantity = computed(() => this.entriesValue().reduce((sum, entry) => sum + (entry.requiredQuantityInUnitOfMeasure || 0), 0));

  allIngredientsAdded = computed(() => {
    const nodes = this.ingredientNodes();
    const ingredients = nodes.filter((n) => n.type === 'ingredient' && n.itemCode);
    return ingredients.length > 0 && ingredients.every((n) => this.isEntryAlreadyAdded(n));
  });

  availableAdditionalItems = computed(() => {
    const recipeItemCodes = this.ingredientNodes()
      .map((node) => node.itemCode)
      .filter((code): code is string => !!code);
    const excluded = new Set([...recipeItemCodes, ...this.entryItemCodes()]);
    return this.additionalItems().filter((item) => !excluded.has(item.itemCode));
  });

  ngOnInit(): void {
    this.statuses = this.enumService.getEnum('RequisitionStatus', 'inventory').values;
    this.statusesIcon = this.enumService.getEnum('RequisitionStatus', 'inventory').valuesEnglish;
    this.statusEnumObject = this.enumService.getEnum('RequisitionStatus', 'inventory');
    this.unitOfMeasures = this.enumService.getEnum('UnitOfMeasure', 'item').values;
    this.pwoStatusLabels = this.enumService.getEnum('ProductionWorkOrderStatus', 'production').values;
    this.batchStatusLabels = this.enumService.getEnum('BatchStatus', 'production').values;
    this.userLite = this.userAuthService.userLite;

    this.route.queryParams.subscribe((params) => {
      const id = params['id'];
      if (id && id !== 'new') {
        this.isCreateMode.set(false);
        this.loadRequisition(id);
      } else {
        this.isCreateMode.set(true);
        this.breadcrumbService.setCurrent('Common_New');
        const pwoIdParam = params['pwoId'];
        const batchIdParam = params['batchId'];
        if (pwoIdParam) {
          this.autoLoadPwoFromParam(pwoIdParam, batchIdParam);
        }
      }
    });

    this.statusControl.valueChanges.subscribe((newStatus) => {
      if (newStatus === null || newStatus === this.originalStatus) return;
      this.confirmStatusChange(newStatus);
    });
  }

  validPwoStatusHint = computed(() => VALID_PWO_STATUSES.map((s) => this.pwoStatusLabels[s] ?? ProductionWorkOrderStatus[s]).join(', '));

  validBatchStatusHint = computed(() => VALID_BATCH_STATUSES.map((s) => this.batchStatusLabels[s] ?? BatchStatus[s]).join(', '));

  private autoLoadPwoFromParam(pwoId: string, batchId?: string): void {
    this.increaseLoading();
    this.pwoProxyService
      .getById(pwoId, true)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const pwo = result?.data;
          if (!pwo) return;

          if (!VALID_PWO_STATUSES.includes(pwo.status as ProductionWorkOrderStatus)) {
            GlobalMessageUtil.warn(
              this.translate('Warning', 'Warning'),
              this.translate('PWO_Invalid_Status', 'PWO {{number}} is not in an eligible status. Allowed: {{allowed}}.', {
                number: pwo.number,
                allowed: this.validPwoStatusHint(),
              }),
            );
            return;
          }

          this.productionWorkOrder.set(pwo);
          this.pwoSearchControl.setValue(pwo, { emitEvent: false });
          this.loadBatchesForPWO(pwo.id, batchId);
        },
      });
  }

  // #region PWO Search & Batch Selection

  public fetchPWOs = (query: string): Observable<ProductionWorkOrder[]> => {
    this.increaseLoading();

    return this.pwoProxyService.search({ number: query, status: VALID_PWO_STATUSES }, 1, 15).pipe(
      finalize(() => this.decreaseLoading()),
      map((result) => result?.data?.items || []),
    );
  };

  onPWOSelected(event: AutoCompleteSelectEvent): void {
    const pwo = event.value as ProductionWorkOrder;
    this.productionWorkOrder.set(pwo);
    this.selectedBatch.set(null);
    this.loadBatchesForPWO(pwo.id);
  }

  private loadBatchesForPWO(pwoId: string, autoSelectBatchId?: string): void {
    this.increaseLoading();
    this.batchProxyService
      .getByInitiatorReference(pwoId)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const eligible = (result?.data || []).filter((b) => VALID_BATCH_STATUSES.includes(b.status as BatchStatus));
          this.batches.set(eligible);

          if (autoSelectBatchId) {
            const match = eligible.find((b) => b.id === autoSelectBatchId);
            if (match) {
              this.selectedBatch.set(match);
            } else {
              GlobalMessageUtil.warn(
                this.translate('Warning', 'Warning'),
                this.translate('Batch_Not_Eligible', 'The requested batch is not in an eligible status. Allowed: {{allowed}}.', {
                  allowed: this.validBatchStatusHint(),
                }),
              );
            }
          }
        },
      });
  }

  selectBatch(batch: Batch): void {
    this.selectedBatch.set(batch);
  }

  // #endregion

  // #region Data Loading

  private loadRequisition(id: string): void {
    this.increaseLoading();
    this.requisitionProxyService
      .getById(id)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const req = result?.data;
          if (!req) return;

          this.requisition.set(req);
          this.notes = req.notes || [];
          this.originalStatus = req.status;
          this.statusControl.setValue(req.status, { emitEvent: false });
          this.breadcrumbService.setCurrent(req.requisitionNumber);
          this.entriesDirty = false;

          this.populateEntriesFromRequisition(req);
          this.loadAdditionalItems(req);

          if (req.initiator?.reference) {
            this.loadBatchAndPWO(req.initiator.reference);
          }

          if (req.id) {
            this.loadAssignedLotItems(req.id);
          }
        },
      });
  }

  private loadAssignedLotItems(requisitionId: string): void {
    const entityKey: EntityKeyByReference = {
      entityDefinitionCode: GlobalEntityDefinition.InternalRequisition,
      reference: requisitionId,
    };

    this.inventoryTransactionProxyService.getRelatedInventoryTransactionsByEntityKey(entityKey, true, true).subscribe({
      next: (result) => {
        const transactions = result?.data || [];
        const grouped = new Map<string, AssignedLotItem[]>();

        for (const tx of transactions) {
          for (const line of tx.lines || []) {
            if (line.lotItem && line.lotItem.itemCode) {
              const existing = grouped.get(line.lotItem.itemCode) || [];
              existing.push({ lotItem: line.lotItem, transactionQuantity: line.quantity });
              grouped.set(line.lotItem.itemCode, existing);
            }
          }
        }

        this.assignedLotItems.set(grouped);
      },
    });
  }

  private loadBatchAndPWO(batchId: string): void {
    this.increaseLoading();
    this.batchProxyService
      .getById(batchId)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const batch = result?.data;
          if (!batch) return;

          this.selectedBatch.set(batch);
          this.loadBatchRecipe(batch.productDevelopmentNumber);

          if (batch.initiator?.reference) {
            this.loadPWODetails(batch.initiator.reference);
          }
        },
      });
  }

  private loadBatchRecipe(recipeNumber: string | null | undefined): void {
    this.selectedBatchRecipe.set(null);
    if (!recipeNumber) return;

    this.productRecipeProxyService.getByNumbers([recipeNumber]).subscribe({
      next: (result) => {
        this.selectedBatchRecipe.set(result?.data?.[0] ?? null);
      },
    });
  }

  private loadPWODetails(pwoId: string): void {
    this.increaseLoading();
    this.pwoProxyService
      .getById(pwoId, true)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const pwo = result?.data;
          if (!pwo) return;

          this.productionWorkOrder.set(pwo);
          this.buildIngredientNodes(pwo);
        },
      });
  }

  private buildIngredientNodes(pwo: ProductionWorkOrder): void {
    const nodes: IngredientDisplayNode[] = [];
    const allItemCodes: string[] = [];
    const subRecipeNumbers = new Set<string>();
    const batch = this.selectedBatch();
    const plannedQty = batch?.plannedQuantity || 0;

    for (const item of pwo.items) {
      const recipe = item.productRecipe;
      if (!recipe) continue;

      const scaleFactor = recipe.baseQuantity > 0 ? plannedQty / recipe.baseQuantity : 1;

      for (const ingredient of recipe.ingredients) {
        if (ingredient.itemCode) {
          allItemCodes.push(ingredient.itemCode);
          nodes.push({
            type: 'ingredient',
            itemCode: ingredient.itemCode,
            productRecipeNumber: ingredient.productRecipeNumber || null,
            itemDisplayName: ingredient.itemCode,
            quantity: ingredient.quantity,
            expectedQuantity: ingredient.quantity * scaleFactor,
            recipeBaseQuantity: recipe.baseQuantity,
            uom: ingredient.uom,
            recipeOutputItemCode: recipe.outputItemCode,
            recipeName: recipe.name || recipe.number,
          });
        } else if (ingredient.productRecipeNumber) {
          // Sub-recipe ingredient: surface the row but defer item-code resolution until the
          // sub-recipe's OutputItemCode is fetched. If missing, the Add button stays disabled.
          subRecipeNumbers.add(ingredient.productRecipeNumber);
          nodes.push({
            type: 'ingredient',
            itemCode: null,
            productRecipeNumber: ingredient.productRecipeNumber,
            itemDisplayName: ingredient.productRecipeNumber,
            quantity: ingredient.quantity,
            expectedQuantity: ingredient.quantity * scaleFactor,
            recipeBaseQuantity: recipe.baseQuantity,
            uom: ingredient.uom,
            recipeOutputItemCode: recipe.outputItemCode,
            recipeName: recipe.name || recipe.number,
            isRecipeIngredient: true,
          });
        }

        for (const sub of ingredient.substitutes || []) {
          if (sub.itemCode) {
            allItemCodes.push(sub.itemCode);
          } else if (sub.productRecipeNumber) {
            subRecipeNumbers.add(sub.productRecipeNumber);
          }
          nodes.push({
            type: 'substitute',
            itemCode: sub.itemCode || null,
            productRecipeNumber: sub.productRecipeNumber || null,
            itemDisplayName: sub.itemCode || sub.productRecipeNumber || '',
            quantity: ingredient.quantity,
            expectedQuantity: ingredient.quantity * scaleFactor,
            recipeBaseQuantity: recipe.baseQuantity,
            uom: ingredient.uom,
            recipeOutputItemCode: recipe.outputItemCode,
            recipeName: recipe.name || recipe.number,
            priority: sub.priority,
            isRecipeIngredient: !sub.itemCode && !!sub.productRecipeNumber,
          });
        }
      }
    }

    this.ingredientNodes.set(nodes);
    allItemCodes.push(...this.entryItemCodes());

    if (subRecipeNumbers.size > 0) {
      this.resolveSubRecipes([...subRecipeNumbers], allItemCodes);
    } else if (allItemCodes.length > 0) {
      this.resolveItemNames([...new Set(allItemCodes)]);
    }
  }

  private resolveSubRecipes(numbers: string[], itemCodesFromDirectIngredients: string[]): void {
    this.productRecipeProxyService.getByNumbers(numbers).subscribe({
      next: (result) => {
        const recipes = result?.data || [];
        const recipeByNumber = new Map(recipes.map((r) => [r.number, r]));
        const additionalItemCodes: string[] = [];

        const patched = this.ingredientNodes().map((node) => {
          if (!node.isRecipeIngredient || !node.productRecipeNumber) return node;
          const subRecipe = recipeByNumber.get(node.productRecipeNumber);
          if (!subRecipe) return node;

          if (subRecipe.outputItemCode) {
            additionalItemCodes.push(subRecipe.outputItemCode);
            return {
              ...node,
              itemCode: subRecipe.outputItemCode,
              subRecipeName: subRecipe.name,
              itemDisplayName: subRecipe.outputItemCode,
            };
          }
          return {
            ...node,
            subRecipeName: subRecipe.name,
            outputItemCodeMissing: true,
          };
        });

        this.ingredientNodes.set(patched);

        const allCodes = [...new Set([...itemCodesFromDirectIngredients, ...additionalItemCodes])];
        if (allCodes.length > 0) {
          this.resolveItemNames(allCodes);
        }
      },
    });
  }

  private resolveItemNames(itemCodes: string[]): void {
    this.itemMasterProxyService.getByItemCodes(itemCodes).subscribe({
      next: (result) => {
        const items = result?.data || [];
        const nameMap = new Map<string, string>();
        for (const item of items) {
          const displayName = this.localHelperService.getLocalizedValue(item.itemDisplayName) || item.itemCode;
          nameMap.set(item.itemCode, displayName);
        }
        this.resolvedItemNames.update((current) => new Map([...current, ...nameMap]));

        const updatedNodes = this.ingredientNodes().map((node) => ({
          ...node,
          itemDisplayName: (node.itemCode && nameMap.get(node.itemCode)) || node.itemDisplayName,
        }));
        this.ingredientNodes.set(updatedNodes);
      },
    });
  }

  // #endregion

  // #region Entries Management

  private populateEntriesFromRequisition(req: Requisition): void {
    this.entriesFormArray.clear();
    for (const entry of req.requisitionEntries) {
      this.entriesFormArray.push(this.createEntryFormGroup(entry));
    }
  }

  private createEntryFormGroup(entry?: Partial<RequisitionEntry>): FormGroup<RequisitionEntryFormGroup> {
    return new FormGroup<RequisitionEntryFormGroup>({
      itemCode: new FormControl<string>(entry?.itemCode || '', { nonNullable: true, validators: [Validators.required] }),
      materialForItemCode: new FormControl<string>(entry?.materialForItemCode || '', { nonNullable: true }),
      unitOfMeasure: new FormControl<number>(entry?.unitOfMeasure || 0, { nonNullable: true }),
      requiredQuantityInUnitOfMeasure: new FormControl<number | null>(entry?.requiredQuantityInUnitOfMeasure || null, {
        validators: [Validators.required, Validators.min(0.01)],
      }),
      providerReference: new FormControl<string>(entry?.providerReference || '', { nonNullable: true }),
      providerLotNumber: new FormControl<string>(entry?.providerLotNumber || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      manufacturer: new FormControl<Business | null>(entry?.manufacturer || null, { validators: [Validators.required] }),
    });
  }

  public fetchManufacturers = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Manufacturer] }, 1, 15)
      .pipe(map((result) => result?.data?.items || []));

  // #region Lot Item Management

  public fetchLotItems = (query: string): Observable<LotItemSearchResultItem[]> => {
    const targetItemCode = this.lotItemTargetItemCode.value;
    if (!targetItemCode) return of([]);

    const entry = this.entriesFormArray.controls.find((g) => g.get('itemCode')?.value === targetItemCode);
    if (!entry) return of([]);

    const manufacturerRef = entry.get('manufacturer')?.value?.id;
    const lotNumber = entry.get('providerLotNumber')?.value;

    const command: LotItemSearchCommand = {
      itemCodes: [targetItemCode],
      locationPaths: [LOT_ITEM_SEARCH_LOCATION],
      excludeZeroBalances: true,
      ...(manufacturerRef ? { manufacturerReferences: [manufacturerRef] } : {}),
      ...(lotNumber ? { manufacturerLotNumbers: [lotNumber] } : {}),
      ...(query ? { lotItemNumbers: [query] } : {}),
    };

    return this.lotItemProxyService.search(command, 1, 25).pipe(map((result) => result?.data?.items || []));
  };

  onLotItemSelected(event: AutoCompleteSelectEvent): void {
    const lotItem = event.value as LotItem;
    const targetItemCode = this.lotItemTargetItemCode.value;
    if (!lotItem?.number || !targetItemCode) return;

    const req = this.requisition();
    if (!req?.id) return;

    const lotItemKey: EntityKeyByNumber = {
      number: lotItem.number,
      entityDefinitionCode: GlobalEntityDefinition.LotItem,
    };

    this.increaseLoading();
    this.requisitionProxyService
      .addLotItem(req.id, lotItemKey, targetItemCode)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Success', 'Success'),
            this.translate('Lot_Item_Added', 'Lot item {{number}} added.', { number: lotItemKey.number }),
          );
          this.lotItemSearchControl.reset('');
          if (req.id) this.loadAssignedLotItems(req.id);
        },
      });
  }

  removeLotItem(lotItem: LotItem): void {
    const req = this.requisition();
    if (!req?.id || !lotItem.number) return;

    const lotItemKey: EntityKeyByNumber = {
      number: lotItem.number,
      entityDefinitionCode: GlobalEntityDefinition.LotItem,
    };

    this.increaseLoading();
    this.requisitionProxyService
      .removeLotItem(req.id, lotItemKey)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Success', 'Success'),
            this.translate('Lot_Item_Removed', 'Lot item {{number}} removed.', { number: lotItemKey.number }),
          );
          if (req.id) this.loadAssignedLotItems(req.id);
        },
      });
  }

  getLotItemsForEntry(itemCode: string): AssignedLotItem[] {
    return this.assignedLotItems().get(itemCode) || [];
  }

  // #endregion

  addToEntries(node: IngredientDisplayNode): void {
    if (this.isEntryAlreadyAdded(node)) return;
    // Sub-recipe ingredients without a resolved OutputItemCode aren't addable; the item
    // master needs to be linked on the recipe first.
    if (!node.itemCode || node.outputItemCodeMissing) return;

    const entry: Partial<RequisitionEntry> = {
      itemCode: node.itemCode,
      materialForItemCode: node.recipeOutputItemCode,
      unitOfMeasure: node.uom,
      requiredQuantityInUnitOfMeasure: node.expectedQuantity || undefined,
    };

    this.entriesFormArray.push(this.createEntryFormGroup(entry));
    this.entriesDirty = true;
  }

  private loadAdditionalItems(req: Requisition): void {
    if (!this.canEdit() || req.status !== RequisitionStatus.Requested || this.additionalItems().length > 0) return;

    this.requisitionProxyService.getAdditionalItems().subscribe({
      next: (result) => {
        this.additionalItems.set(result?.data || []);
      },
    });
  }

  getItemDisplayName(item: ItemMaster): string {
    return this.localHelperService.getLocalizedValue(item.itemDisplayName) || item.itemCode;
  }

  addAdditionalItemToEntries(): void {
    const item = this.additionalItemControl.value;
    if (!item) return;

    if (this.entryItemCodes().includes(item.itemCode)) {
      GlobalMessageUtil.warn(
        this.translate('Warning', 'Warning'),
        this.translate('Additional_Item_Already_Added', 'Item {{itemCode}} is already in the requisition entries.', {
          itemCode: item.itemCode,
        }),
      );
      this.additionalItemControl.reset();
      return;
    }

    this.resolvedItemNames.update((names) => new Map(names).set(item.itemCode, this.getItemDisplayName(item)));
    this.entriesFormArray.push(
      this.createEntryFormGroup({
        itemCode: item.itemCode,
        unitOfMeasure: item.unitOfMeasure,
      }),
    );
    this.entriesDirty = true;
    this.additionalItemControl.reset();
  }

  addAllToEntries(): void {
    const nodes = this.ingredientNodes().filter((n) => n.type === 'ingredient' && n.itemCode && !this.isEntryAlreadyAdded(n));
    for (const node of nodes) {
      this.addToEntries(node);
    }
  }

  removeEntry(index: number): void {
    this.entriesFormArray.removeAt(index);
    this.entriesDirty = true;
  }

  isEntryAlreadyAdded(node: IngredientDisplayNode): boolean {
    return this.entriesFormArray.controls.some((group) => {
      const itemCode = group.get('itemCode')?.value;
      const materialFor = group.get('materialForItemCode')?.value;
      return itemCode === node.itemCode && materialFor === node.recipeOutputItemCode;
    });
  }

  getItemName(itemCode: string): string {
    return this.resolvedItemNames().get(itemCode) || itemCode;
  }

  // #endregion

  // #region Save & Status

  save(): void {
    if (this.isCreateMode()) {
      this.createRequisition();
    } else {
      this.patchEntries();
    }
  }

  private createRequisition(): void {
    const batch = this.selectedBatch();
    if (!batch) return;

    const entityKey: EntityKeyByReference = {
      entityDefinitionCode: ENTITY_DEF_BATCH,
      reference: batch.id,
    };

    this.increaseLoading();
    this.requisitionProxyService
      .createFromEntityKey(entityKey, FROM_LOCATION_CODE, TO_LOCATION_CODE)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const created = result?.data;
          if (created) {
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Requisition_Created', "Requisition '{{number}}' created successfully.", {
                number: created.requisitionNumber ?? '',
              }),
            );
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { id: created.id },
              queryParamsHandling: 'replace',
            });
          }
        },
      });
  }

  patchEntries(): void {
    const req = this.requisition();
    if (!req?.id) return;

    if (!this.entriesFormArray.valid) {
      GlobalMessageUtil.warn(this.translate('Warning', 'Warning'), this.translate('Form_Invalid', 'Please fill in all required fields.'));
      return;
    }

    const entries = this.getEntriesPayload();

    this.increaseLoading();
    this.requisitionProxyService
      .patchEntries(req.id, entries)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const updated = result?.data;
          if (updated) {
            this.requisition.set(updated);
            this.populateEntriesFromRequisition(updated);
            this.entriesDirty = false;
            GlobalMessageUtil.success(
              this.translate('Success', 'Success'),
              this.translate('Entries_Saved', 'Requisition entries saved successfully.'),
            );
          }
        },
      });
  }

  private confirmStatusChange(newStatus: number): void {
    const isGoingToProcessing = newStatus === RequisitionStatus.Processing;
    let message = this.translate('Confirm_Status_Change', 'Are you sure you want to change the status to "{{status}}"?', {
      status: this.statuses[newStatus],
    });

    if (isGoingToProcessing && this.entriesDirty) {
      message +=
        '\n\n' +
        this.translate(
          'Save_Entries_Warning',
          'You have unsaved changes to requisition entries. Please save them before changing to Processing status.',
        );
      GlobalMessageUtil.warn(
        this.translate('Warning', 'Warning'),
        this.translate('Save_Entries_First', 'Please save requisition entries before changing to Processing.'),
      );
      this.statusControl.setValue(this.originalStatus, { emitEvent: false });
      return;
    }

    this.confirmationService.confirm({
      message,
      header: this.translate('Confirm', 'Confirm'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.executeStatusChange(newStatus),
      reject: () => this.statusControl.setValue(this.originalStatus, { emitEvent: false }),
    });
  }

  private executeStatusChange(newStatus: number): void {
    const req = this.requisition();
    if (!req?.id) return;

    this.increaseLoading();
    this.requisitionProxyService
      .updateStatus(req.id, newStatus)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Success', 'Success'),
            this.translate('Requisition_Status_Updated', 'Requisition status updated to {{status}}.', { status: this.statuses[newStatus] }),
          );
          if (req.id) this.loadRequisition(req.id);
        },
        error: () => {
          this.statusControl.setValue(this.originalStatus, { emitEvent: false });
        },
      });
  }

  // #endregion

  // #region Helpers

  private getEntriesPayload(): RequisitionEntry[] {
    return this.entriesFormArray.controls.map((group) => {
      const val = group.getRawValue();
      return {
        itemCode: val.itemCode,
        materialForItemCode: val.materialForItemCode,
        unitOfMeasure: val.unitOfMeasure,
        requiredQuantityInUnitOfMeasure: val.requiredQuantityInUnitOfMeasure || 0,
        providerReference: val.manufacturer?.id || val.providerReference,
        providerLotNumber: val.providerLotNumber,
      };
    });
  }

  private translate(key: string, fallback: string, params?: Record<string, string>): string {
    const translation = this.translateService.instant(key, params);
    if (translation !== key) {
      return translation;
    }
    if (params) {
      let result = fallback;
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      }
      return result;
    }
    return fallback;
  }

  get isLoading(): boolean {
    return this.loading() > 0;
  }

  private increaseLoading(): void {
    this.loading.update((n) => n + 1);
  }

  private decreaseLoading(): void {
    this.loading.update((n) => Math.max(0, n - 1));
  }

  goBack(): void {
    this.router.navigate(['/requisitions']);
  }

  get validStatusOptions(): EnumValues {
    const currentStatus = this.requisition()?.status;
    const allStatus = this.statuses ?? {};
    switch (currentStatus) {
      case RequisitionStatus.Requested:
        return this.filterStatuses(allStatus, [RequisitionStatus.Rejected, RequisitionStatus.InRoute, RequisitionStatus.Received]);
      case RequisitionStatus.Processing:
        return this.filterStatuses(allStatus, [RequisitionStatus.Requested, RequisitionStatus.Rejected, RequisitionStatus.Received]);
      case RequisitionStatus.InRoute:
        return this.filterStatuses(allStatus, [RequisitionStatus.Requested, RequisitionStatus.Processing]);
      case RequisitionStatus.Rejected:
        return this.filterStatuses(allStatus, [RequisitionStatus.Requested, RequisitionStatus.InRoute, RequisitionStatus.Received]);
      case RequisitionStatus.Received:
      case RequisitionStatus.Voided:
        return {};
      default:
        return allStatus;
    }
  }

  private filterStatuses(allStatus: EnumValues, exclude: number[]): EnumValues {
    return Object.keys(allStatus)
      .filter((key) => !isNaN(Number(key)) && !exclude.includes(Number(key)))
      .reduce((acc, key) => {
        acc[Number(key)] = allStatus[Number(key)];
        return acc;
      }, {} as EnumValues);
  }

  // #endregion
}
